import { useEffect, useRef } from 'react';
import { useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { heightRE, lengthRE, widthRE } from '../constants';
import { getAnchorItemIds, getMetadataAttributeValues, getSampleColorSpecValue } from '../helpers';

export const useOrderSample = ({ itemId, storeId, zipCode }) => {
  const excludedAnchorItemIdsRef = useRef([]);
  const anchorItemIdRef = useRef(itemId);

  const [productReq, productResponse] = useLazyDataModel('clientOnlyProduct', {});

  useEffect(() => {
    anchorItemIdRef.current = itemId;
    productReq({
      skip: storeId === '8119' || !itemId,
      ssr: false,
      variables: {
        itemId
      },
    });
  }, [itemId]);
  
  const productData = productResponse?.data;
  const { sampleId, isSuperSku, parentId } = productData?.product?.identifiers || {};

  const [metadataReq, metadataResponse] = useLazyDataModel('sampleProductsMetadata', {
    skip: !parentId,
    ssr: false
  });
  
  const sampleProductOptions = {
    skip: storeId === '8119' || !sampleId || sampleId === anchorItemIdRef.current || !zipCode,
    ssr: false,
    variables: {
      itemId: sampleId,
      storeId,
      zipCode
    },
  };
  const sample = useDataModel('sampleProduct', sampleProductOptions);
  
  let dimensionText = '';
  
  const sampleProduct = sample?.data?.product;
  const samplePrice = sampleProduct?.pricing?.value;

  // There are MANY data issues in anchor and sample products
  // The useEffect below ensures that we only pull valid samples (if possible)
  useEffect(() => {
    const productHasInvalidPrice = !sampleId // sample needs sampleId
        // sampleId should not equal the anchor itemId
        || sampleId === anchorItemIdRef.current
        /*
          sample needs to have a price
          * Discontinued samples are okay without a price, but due to bad data
          * we will to try to find a sample with a price for this SKU where possible
        */
        || (sampleProduct?.identifiers?.itemId === sampleId && !samplePrice);
    if (productHasInvalidPrice && productData) {
      if (!metadataResponse.data?.metadata) {
        metadataReq({ variables: { parentId } });
      } else {
        const metadata = metadataResponse.data.metadata;
        const skuNames = getMetadataAttributeValues(metadata.attributes);
        const thisSkuName = getSampleColorSpecValue(productData.product, skuNames);
        excludedAnchorItemIdsRef.current = [...excludedAnchorItemIdsRef.current, anchorItemIdRef.current];
        if (thisSkuName) {
          const anchorItemIds = getAnchorItemIds({
            metadata,
            specificSkuNames: [thisSkuName],
            excludedItemIds: excludedAnchorItemIdsRef.current
          });
          if (anchorItemIds.length) {
            anchorItemIdRef.current = anchorItemIds[0];
            productReq({
              skip: storeId === '8119' || !anchorItemIdRef.current,
              ssr: false,
              variables: {
                itemId: anchorItemIdRef.current
              },
            });
          }
        }
      }
    }
  }, [metadataResponse.data?.metadata, productData, sampleProduct]);

  if (sampleProduct?.specificationGroup) {
    const { specifications: dimensions } = sampleProduct.specificationGroup
      .find((spec) => spec.specTitle === 'Dimensions') || {};
    
    if (dimensions?.length > 0) {
      const length = dimensions.find(({ specName }) => lengthRE.test(specName) || heightRE.test(specName));
      const width = dimensions.find(({ specName }) => widthRE.test(specName));
      
      if (length && width) {
        const getDimensionText = (dimension) => {
          const specExt = dimension?.specName?.match(/\(([^)]+)\)/);
          const specName = specExt.length > 0 ? specExt[1] : '';
          return `${(dimension.specValue.match(/\d+/g) || [])[0]} ${specName}`;
        };
        const lengthText = getDimensionText(length);
        const widthText = getDimensionText(width);
        dimensionText = `${lengthText} x ${widthText}`;
      }
    }
  }

  return {
    data: {
      dimensionText,
      discontinued: !!sampleProduct?.availabilityType?.discontinued,
      isSuperSku,
      parentId,
      price: samplePrice,
      sampleProduct,
      sampleId
    },
    loading: productResponse.loading || sample?.loading
  };
};
